import type { OcSelfserviceSettings } from "@oc/types";

const OC_API_BASE = import.meta.env.VITE_OC_API_BASE ?? "/api";

export const OC_SESSION_KEY = "oc_session_token";
const OC_SESSION_CREATED_KEY = "oc_session_created";
const OC_LAST_ACTIVITY_KEY = "oc_last_activity";

export const OC_SESSION_MAX_MS = 24 * 60 * 60 * 1000;   // 24 hours
export const OC_INACTIVITY_MAX_MS = 8 * 60 * 60 * 1000; // 8 hours

export function ocGetSessionToken(): string | null {
  return localStorage.getItem(OC_SESSION_KEY);
}

export function ocSetSessionToken(token: string): void {
  const now = String(Date.now());
  localStorage.setItem(OC_SESSION_KEY, token);
  localStorage.setItem(OC_SESSION_CREATED_KEY, now);
  localStorage.setItem(OC_LAST_ACTIVITY_KEY, now);
}

export function ocClearSessionToken(): void {
  localStorage.removeItem(OC_SESSION_KEY);
  localStorage.removeItem(OC_SESSION_CREATED_KEY);
  localStorage.removeItem(OC_LAST_ACTIVITY_KEY);
}

export function ocRecordActivity(): void {
  localStorage.setItem(OC_LAST_ACTIVITY_KEY, String(Date.now()));
}

export function ocIsSessionExpired(): boolean {
  const now = Date.now();
  const created = Number(localStorage.getItem(OC_SESSION_CREATED_KEY) ?? "0");
  const lastActivity = Number(localStorage.getItem(OC_LAST_ACTIVITY_KEY) ?? created);
  if (created > 0 && now - created > OC_SESSION_MAX_MS) return true;
  if (now - lastActivity > OC_INACTIVITY_MAX_MS) return true;
  return false;
}

export class OcApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    // Parsed JSON body of the error response, if any. Callers can read
    // structured fields like `error` and `missing` for preconditions failures.
    public readonly body?: unknown
  ) {
    super(message);
    this.name = "OcApiError";
  }
}

export async function ocFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const extraHeaders: Record<string, string> = {};
  const token = ocGetSessionToken();
  if (token) extraHeaders["X-OC-Session-Token"] = token;

  const response = await fetch(`${OC_API_BASE}${path}`, {
    headers: { "Content-Type": "application/json", ...extraHeaders, ...options?.headers },
    ...options,
  });
  if (!response.ok) {
    const text = await response.text().catch(() => "Unknown error");
    let parsed: unknown;
    try { parsed = JSON.parse(text); } catch { /* not JSON */ }
    throw new OcApiError(response.status, `${response.status}: ${text}`, parsed);
  }
  return response.json() as Promise<T>;
}

export function ocApiKey(path: string): readonly ["oc-api", string] {
  return ["oc-api", path] as const;
}

/** Convenience wrapper for POST/PUT/PATCH with JSON body. */
export async function ocMutate<TBody, TResponse>(
  path: string,
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  body?: TBody
): Promise<TResponse> {
  return ocFetch<TResponse>(path, {
    method,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
}

// ─── Namespaced API surface ───────────────────────────────────────────────────

export interface OcSelfserviceEligibleConnection {
  id: string;
  name: string;
  type: string;
  hasPasswordReset: boolean;
}

export interface OcSelfserviceTwilioConnectionRef {
  id: string;
  name: string;
}

export interface OcSelfserviceSmtpConnectionRef {
  id: string;
  name: string;
  provider: string;
}

export interface OcSelfserviceSettingsResponse {
  settings: OcSelfserviceSettings;
  eligibleConnections: OcSelfserviceEligibleConnection[];
  twilioConnections: OcSelfserviceTwilioConnectionRef[];
  smtpConnections: OcSelfserviceSmtpConnectionRef[];
}

// ─── Reset Log types ──────────────────────────────────────────────────────────

export type OcSelfserviceResetLogStatus =
  | "pending"
  | "verified"
  | "success"
  | "failed"
  | "expired"
  | "blocked";

export interface OcSelfserviceResetLogEntry {
  id: string;
  tenantId: string;
  createdAt: string;
  completedAt: string | null;
  identifierSubmitted: string | null;
  resolvedUserDn: string | null;
  connectionId: string | null;
  proofingMethod: string | null;
  proofingTargetMasked: string | null;
  verifyAttempts: number | null;
  ip: string | null;
  userAgent: string | null;
  country: string | null;
  status: OcSelfserviceResetLogStatus;
  failureReason: string | null;
  dispatchedCommandId: string | null;
  durationMs: number | null;
}

export interface OcSelfserviceResetLogSparklinePoint {
  date: string; // YYYY-MM-DD
  success: number;
  failed: number;
  expired: number;
  blocked: number;
}

export interface OcSelfserviceResetLogPatterns {
  repeatResets: {
    count: number;
    users: Array<{ resolvedUserDn: string; resetCount: number }>;
  };
  failedVerifySpike: {
    count: number;
    ips: Array<{ ip: string; attemptCount: number; country: string | null }>;
  };
  resetThenPrivChange: {
    count: number;
    pairs: Array<{ resolvedUserDn: string; resetAt: string; privChangeAt: string }>;
    note?: string;
  };
}

export interface OcSelfserviceResetLogResponse {
  rows: OcSelfserviceResetLogEntry[];
  total: number;
  page: number;
  pageSize: number;
  sparkline: OcSelfserviceResetLogSparklinePoint[];
  patterns: OcSelfserviceResetLogPatterns;
}

export interface OcSelfserviceResetLogQueryParams {
  from?: string;
  to?: string;
  status?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}

function ocBuildResetLogQuery(
  params: OcSelfserviceResetLogQueryParams,
  extra?: Record<string, string>
): string {
  const search = new URLSearchParams();
  if (params.from) search.set("from", params.from);
  if (params.to) search.set("to", params.to);
  if (params.status) search.set("status", params.status);
  if (params.q) search.set("q", params.q);
  if (params.page !== undefined) search.set("page", String(params.page));
  if (params.pageSize !== undefined) search.set("pageSize", String(params.pageSize));
  if (extra) {
    for (const [k, v] of Object.entries(extra)) search.set(k, v);
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export const ocApi = {
  selfservice: {
    getSettings: (): Promise<OcSelfserviceSettingsResponse> =>
      ocFetch<OcSelfserviceSettingsResponse>("/tenant/selfservice/settings"),
    updateSettings: (
      patch: Partial<OcSelfserviceSettings>
    ): Promise<{ settings: OcSelfserviceSettings }> =>
      ocMutate<Partial<OcSelfserviceSettings>, { settings: OcSelfserviceSettings }>(
        "/tenant/selfservice/settings",
        "PUT",
        patch
      ),
    getResetLog: (
      params: OcSelfserviceResetLogQueryParams = {}
    ): Promise<OcSelfserviceResetLogResponse> =>
      ocFetch<OcSelfserviceResetLogResponse>(
        `/tenant/selfservice/reset-log${ocBuildResetLogQuery(params)}`
      ),
    downloadResetLogCsv: async (
      params: Omit<OcSelfserviceResetLogQueryParams, "page" | "pageSize"> = {}
    ): Promise<Blob> => {
      const token = ocGetSessionToken();
      const headers: Record<string, string> = {};
      if (token) headers["X-OC-Session-Token"] = token;
      const qs = ocBuildResetLogQuery(params, { format: "csv" });
      const response = await fetch(
        `${OC_API_BASE}/tenant/selfservice/reset-log${qs}`,
        { headers }
      );
      if (!response.ok) {
        const text = await response.text().catch(() => "Unknown error");
        throw new OcApiError(response.status, `${response.status}: ${text}`);
      }
      return response.blob();
    },
  },
};
